// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { urlLinkSchema } from '@/url-link/api/types';
import { z } from 'zod';

// Response schema for the URL link update endpoint
const updateURLLinkResponseSchema = z.object({
  data: urlLinkSchema,
  metadata: responseMetadataSchema,
});

type UpdateURLLinkResponse = z.infer<typeof updateURLLinkResponseSchema>;

/**
 * Parameters for updating a URL link.
 * Only provide the fields that should be updated.
 */
export interface UpdateURLLinkParams {
  /** The key of the URL link to update */
  urlLinkKey: string;
  /** Display name for the link (optional - only sent if changed) */
  name?: string | null;
  /** URL to link to (optional - only sent if changed) */
  url?: string;
}

/**
 * Updates an existing URL link with new name and/or URL.
 * This function calls the backend endpoint PATCH /v1/url-link/{linkKey}
 * with only the changed URL link details in the request body.
 *
 * At least one field (URL or name) must be provided when updating.
 * Only fields that have been modified will be sent to the server.
 *
 * @param params - The parameters for updating the URL link
 * @returns Promise containing the updated URL link details
 */
export async function updateURLLink(
  params: UpdateURLLinkParams
): Promise<UpdateURLLinkResponse> {
  const apiUrl = `/v1/url-link/${params.urlLinkKey}`;

  // Only include fields that are provided (changed)
  const payload: { name?: string | null; url?: string } = {};

  if (params.name !== undefined) {
    payload.name = params.name;
  }

  if (params.url !== undefined) {
    payload.url = params.url;
  }

  const response = await api.patch(apiUrl, payload);

  return parseResponse(updateURLLinkResponseSchema, response.data, {
    schemaName: 'UpdateURLLink',
    endpoint: apiUrl,
  });
}
